import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, isSupportedMarket } from "@/lib/markets";

export default function EmailGate({
  open,
  onOpenChange,
  tier,
  onSubmit,
  loading,
  // WHERE THE BUSINESS TRADES, ASKED BEFORE STRIPE.
  //
  // The Margin Audit is sold only to Australian businesses, because its
  // comparators and cost tolerances describe that market and nothing else.
  // Checkout runs BEFORE intake, so without this the first time a foreign
  // buyer learns the category comparisons cannot be produced for them is on
  // the intake form, after A$990 has been taken. The backend refuses the
  // session on its own; this is what lets us say so before they click.
  askCountry = false,
  // Prefilled for a signed-in buyer, whose address we already hold. They still
  // answer the country - it is the question the sale now depends on.
  initialEmail = "",
  title = "One line before Stripe.",
  description,
  footnote = "Used for this audit only. Never sold.",
  submitLabel = "Continue",
  loadingLabel = "Opening Stripe…",
  testId = "email-gate",
}) {
  const [email, setEmail] = useState(initialEmail);
  const [country, setCountry] = useState("");

  const unsupported = askCountry && country && !isSupportedMarket(country);

  const submit = (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (askCountry) {
      if (!country) {
        toast.error("Tell us where the business trades");
        return;
      }
      // Blocked in the interface AND refused by the backend. Neither is
      // decorative: this one explains the boundary in place, without a
      // round-trip that ends in a failed checkout.
      if (!isSupportedMarket(country)) return;
    }
    onSubmit(trimmed, country || null);
  };

  const resolvedDescription =
    description ??
    <>We attach your email to the {tier?.name || "checkout"} so the analyst can
      reach you about the audit. Stripe sends the receipt separately.</>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`${testId}-dialog`}
        className="bg-[#faf7f2] border border-[#c9c3b8] text-[#171717] max-w-lg p-0"
      >
        <div className="p-8">
          <DialogHeader className="text-left space-y-2 mb-6">
            <div className="tick flex items-center gap-2">
              <Mail size={12} /> Analyst contact
            </div>
            <DialogTitle className="font-serif-display text-3xl leading-tight text-[#171717]">
              {title}
            </DialogTitle>
            <DialogDescription className="font-mono-tech text-[12px] text-[#525252] leading-relaxed">
              {resolvedDescription}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="tick block mb-2" htmlFor={`${testId}-input`}>Email address</label>
              <input
                id={`${testId}-input`}
                data-testid={`${testId}-input`}
                type="email"
                autoFocus
                className="input-vault"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {askCountry && (
              <div>
                <label className="tick block mb-2" htmlFor={`${testId}-country`}>
                  Where does the business primarily trade?
                </label>
                <select
                  id={`${testId}-country`}
                  data-testid={`${testId}-country`}
                  className="input-vault"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Select…</option>
                  {COUNTRIES.map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
                {unsupported && (
                  <div
                    data-testid={`${testId}-unsupported`}
                    className="mt-3 border border-[#9a3412] bg-[#f5ece6] p-3 font-mono-tech text-[11px] text-[#7c2d12] leading-relaxed"
                  >
                    <strong>The Margin Audit is built for Australian brands.</strong> Its
                    category comparators and cost tolerances describe that market, so we
                    do not sell it for a business trading elsewhere — you would be paying
                    for comparisons we cannot honestly produce for you. The{" "}
                    <strong>free Margin Risk Sweep</strong> reads any storefront and applies
                    no market comparators at all.
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <div className="font-mono-tech text-[11px] text-[#525252]">
                {footnote}
              </div>
              <button type="submit" disabled={loading || unsupported} data-testid={`${testId}-submit`} className="btn-primary">
                {loading ? loadingLabel : (<>{submitLabel} <ArrowRight size={14} /></>)}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
